import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Link, CreateLinkData } from '@/types/link-short';
import { useAuth } from '@/hooks/use-auth';
import { toast } from '@/hooks/use-toast';

export function useLinks(mainLinkId: string) {
  const { user } = useAuth();
  const [links, setLinks] = useState<Link[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLinks = async () => {
    if (!mainLinkId || !user) {
      setLoading(false);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('links')
        .select('*')
        .eq('main_link_id', mainLinkId)
        .eq('user_id', user.id)
        .order('order_index', { ascending: true });

      if (error) throw error;
      
      const transformedData: Link[] = (data || []).map(item => ({
        id: item.id,
        main_link_id: item.main_link_id,
        user_id: item.user_id,
        title: item.title,
        url: item.url,
        icon: item.icon,
        is_active: item.is_active,
        clicks: item.clicks,
        order_index: item.order_index,
        bg_color: item.bg_color,
        text_color: item.text_color,
        link_style: item.link_style as 'default' | 'pill' | 'square' | 'minimal' | 'gradient' | 'glass',
        icon_position: item.icon_position as 'left' | 'right' | 'top' | 'none',
        social_platform: item.social_platform,
        avatar_url: item.avatar_url,
        created_at: item.created_at,
        updated_at: item.updated_at
      }));
      
      setLinks(transformedData);
    } catch (error) {
      console.error('Error fetching links:', error);
      toast.error("Erro ao carregar links");
    } finally {
      setLoading(false);
    }
  };

  const createLink = async (data: CreateLinkData) => {
    if (!user || !mainLinkId) return null;

    try {
      const { data: newLink, error } = await supabase
        .from('links')
        .insert({
          ...data,
          user_id: user.id,
          main_link_id: mainLinkId
        })
        .select()
        .single();

      if (error) throw error;
      
      const transformedLink: Link = {
        id: newLink.id,
        main_link_id: newLink.main_link_id,
        user_id: newLink.user_id,
        title: newLink.title,
        url: newLink.url,
        icon: newLink.icon,
        is_active: newLink.is_active,
        clicks: newLink.clicks,
        order_index: newLink.order_index,
        bg_color: newLink.bg_color,
        text_color: newLink.text_color,
        link_style: newLink.link_style as 'default' | 'pill' | 'square' | 'minimal' | 'gradient' | 'glass',
        icon_position: newLink.icon_position as 'left' | 'right' | 'top' | 'none',
        social_platform: newLink.social_platform,
        avatar_url: newLink.avatar_url,
        created_at: newLink.created_at,
        updated_at: newLink.updated_at
      };
      
      setLinks(prev => [...prev, transformedLink].sort((a, b) => a.order_index - b.order_index));
      toast.success("Link adicionado com sucesso!");
      return transformedLink;
    } catch (error) {
      console.error('Error creating link:', error);
      toast.error("Erro ao criar link");
      return null;
    }
  };

  const updateLink = async (id: string, updates: Partial<Link>) => {
    try {
      const { error } = await supabase
        .from('links')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      
      setLinks(prev => 
        prev.map(link => 
          link.id === id ? { ...link, ...updates } : link
        )
      );
      toast.success("Link atualizado com sucesso!");
      return true;
    } catch (error) {
      console.error('Error updating link:', error);
      toast.error("Erro ao atualizar link");
      return false;
    }
  };

  const deleteLink = async (id: string) => {
    try {
      const { error } = await supabase
        .from('links')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setLinks(prev => prev.filter(link => link.id !== id));
      toast.success("Link excluído com sucesso!");
      return true;
    } catch (error) {
      console.error('Error deleting link:', error);
      toast.error("Erro ao excluir link");
      return false;
    }
  };

  const reorderLinks = async (orderedIds: string[]) => {
    try {
      const updates = orderedIds.map((id, index) => ({
        id,
        order_index: index
      }));

      for (const update of updates) {
        await supabase
          .from('links')
          .update({ order_index: update.order_index })
          .eq('id', update.id);
      }

      setLinks(prev => 
        prev.map(link => ({
          ...link,
          order_index: orderedIds.indexOf(link.id)
        })).sort((a, b) => a.order_index - b.order_index)
      );

      return true;
    } catch (error) {
      console.error('Error reordering links:', error);
      toast.error("Erro ao reordenar links");
      return false;
    }
  };

  const incrementLinkClicks = async (id: string) => {
    try {
      await supabase.rpc('increment_link_clicks', { link_id: id });
    } catch (error) {
      console.error('Error incrementing clicks:', error);
    }
  };

  useEffect(() => {
    fetchLinks();
  }, [mainLinkId, user]);

  return {
    links,
    loading,
    createLink,
    updateLink,
    deleteLink,
    reorderLinks,
    incrementLinkClicks,
    refetch: fetchLinks
  };
}
